<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetCompanyGeneral',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'input']);

  const props = defineProps({
    ...userComponentProps,
    item: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
    displayFetchByTaxNumber: {
      type: Boolean,
      required: false,
      default: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const companyRelations = ref(null);
  const companyGroups = ref(null);
  const defaultOwnCompanySettings = ref({
    smtp_mail_account_id: null,
    send_invoice_notifications: false,
  });
  const defaultPartnerCompanySettings = ref({
    notifications_enabled: null,
    email_recipients: [],
    send_invoice_notifications: false,
  });
  const settings = ref(null);
  const ux = ref([]);
  const b_1490259005804208_bindObject = computed(() => false);

  const $data = computed(() => ({
    companyRelations: companyRelations.value,
    companyGroups: companyGroups.value,
    defaultOwnCompanySettings: defaultOwnCompanySettings.value,
    defaultPartnerCompanySettings: defaultPartnerCompanySettings.value,
    settings: settings.value,
    ux: ux.value,
    b_1490259005804208_bindObject: b_1490259005804208_bindObject.value,
  }));

  const $$load_companyRelations = () => {
    return $fn.value.getEnumValuesFromSchema('CompanyRelation', {
      sort: false,
    });
  };
  const $$load_companyGroups = () => {
    return $fn.value
      .fetch('list_group', { parameters: { type: 'company' } })
      .then((data) => data.data.map((p) => p.name));
  };
  const $$load_settings = () => {
    return _.extend(
      {},
      props.item.role == 'own'
        ? $data.value.defaultOwnCompanySettings
        : $data.value.defaultPartnerCompanySettings,
      props.item.settings
    );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'companyRelations' },
      { name: 'companyGroups' },
      { name: 'defaultOwnCompanySettings' },
      { name: 'defaultPartnerCompanySettings' },
      { name: 'settings' },
      { name: 'ux' },
      { name: 'b_1490259005804208_bindObject', nodeId: '1490259005804208' },
    ],
    loaders: {
      companyRelations: $$load_companyRelations,
      companyGroups: $$load_companyGroups,
      settings: $$load_settings,
    },
    ux: {},
    dataRefs: {
      companyRelations,
      companyGroups,
      defaultOwnCompanySettings,
      defaultPartnerCompanySettings,
      settings,
      ux,
      b_1490259005804208_bindObject,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isOwnCompany = computed(() => {
    return props.item.role === 'own';
  });

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );
  watch(
    () => props.displayFetchByTaxNumber,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('displayFetchByTaxNumber');
    }
  );

  const act_8497951412896552_emit_8o76ozgf9_getActionArgs = async ($event) => {
    return {
      event: 'input',
      value: $event.data.response.data,
    };
  };
  const act_8497951412896552_request_b737m3vz0_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'read_company',
      data: {
        id: props.item.tax_number,
      },
      headers: {
        accept: 'application/vnd.itp.taxpayer-info+json',
      },
    };
  };
  const act_2014513287668498_script_hamw3z3dq_value_callable = async (
    $event
  ) => {
    return (props.item.settings = settings.value);
  };
  const act_2014513287668498_script_hamw3z3dq_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2014513287668498_script_hamw3z3dq_value_callable($event);
      },
    };
  };
  const act_1490259005804208_script_4vufqxysg_value_callable = async (
    $event
  ) => {
    return (props.item.settings = settings.value);
  };
  const act_1490259005804208_script_4vufqxysg_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1490259005804208_script_4vufqxysg_value_callable($event);
      },
    };
  };
  const act_5501809153536538_script_aqe1pyowz_value_callable = async (
    $event
  ) => {
    return (props.item.settings = settings.value);
  };
  const act_5501809153536538_script_aqe1pyowz_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5501809153536538_script_aqe1pyowz_value_callable($event);
      },
    };
  };
  const act_1014170467214631_script_fg9rg73h3_value_callable = async (
    $event
  ) => {
    return (props.item.settings = settings.value);
  };
  const act_1014170467214631_script_fg9rg73h3_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1014170467214631_script_fg9rg73h3_value_callable($event);
      },
    };
  };
  const act_4664876391460503_script_u7twxtn7m_value_callable = async (
    $event
  ) => {
    return (props.item.settings = settings.value);
  };
  const act_4664876391460503_script_u7twxtn7m_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_4664876391460503_script_u7twxtn7m_value_callable($event);
      },
    };
  };

  const act_7315092382398562_reloadComponentData_jk38dk3kd =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_cmlu1uerj =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_jk38dk3kd,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_cmlu1uerj],
    event: 'reload',
  });
  const act_8497951412896552_emit_8o76ozgf9 = new actionTypes.EmitAction({
    actionArgs: act_8497951412896552_emit_8o76ozgf9_getActionArgs,
  });
  const inst_evh_8497951412896552_success_rqbrjdfbh =
    new actionTypes.EventHandlerImpl({
      action: act_8497951412896552_emit_8o76ozgf9,
      event: 'success',
      displayName: 'emit',
    });
  const act_8497951412896552_request_b737m3vz0 = new actionTypes.RequestAction({
    actionArgs: act_8497951412896552_request_b737m3vz0_getActionArgs,
    displayName: 'fetchTaxpayer',
    events: [inst_evh_8497951412896552_success_rqbrjdfbh],
  });
  const inst_evh_8497951412896552_clickPreventStop_sx3oflgu4 =
    new actionTypes.EventHandlerImpl({
      action: act_8497951412896552_request_b737m3vz0,
      event: 'click.prevent.stop',
      displayName: 'fetchTaxpayer',
    });
  const inst_evh_8497951412896552_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8497951412896552_clickPreventStop_sx3oflgu4],
      event: 'click.prevent.stop',
    });
  const act_2014513287668498_script_hamw3z3dq = new actionTypes.ScriptAction({
    actionArgs: act_2014513287668498_script_hamw3z3dq_getActionArgs,
  });
  const inst_evh_2014513287668498_input_o0681t3gg =
    new actionTypes.EventHandlerImpl({
      action: act_2014513287668498_script_hamw3z3dq,
      event: 'input',
      displayName: 'script',
    });
  const inst_evh_2014513287668498_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2014513287668498_input_o0681t3gg],
    event: 'input',
  });
  const act_1490259005804208_script_4vufqxysg = new actionTypes.ScriptAction({
    actionArgs: act_1490259005804208_script_4vufqxysg_getActionArgs,
  });
  const inst_evh_1490259005804208_input_twm0x1l0p =
    new actionTypes.EventHandlerImpl({
      action: act_1490259005804208_script_4vufqxysg,
      event: 'input',
      displayName: 'script',
    });
  const inst_evh_1490259005804208_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1490259005804208_input_twm0x1l0p],
    event: 'input',
  });
  const act_5501809153536538_script_aqe1pyowz = new actionTypes.ScriptAction({
    actionArgs: act_5501809153536538_script_aqe1pyowz_getActionArgs,
  });
  const inst_evh_5501809153536538_input_xubjnqcww =
    new actionTypes.EventHandlerImpl({
      action: act_5501809153536538_script_aqe1pyowz,
      event: 'input',
      displayName: 'script',
    });
  const inst_evh_5501809153536538_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5501809153536538_input_xubjnqcww],
    event: 'input',
  });
  const act_1014170467214631_script_fg9rg73h3 = new actionTypes.ScriptAction({
    actionArgs: act_1014170467214631_script_fg9rg73h3_getActionArgs,
  });
  const inst_evh_1014170467214631_input_j4x293ri8 =
    new actionTypes.EventHandlerImpl({
      action: act_1014170467214631_script_fg9rg73h3,
      event: 'input',
      displayName: 'script',
    });
  const inst_evh_1014170467214631_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1014170467214631_input_j4x293ri8],
    event: 'input',
  });
  const act_4664876391460503_script_u7twxtn7m = new actionTypes.ScriptAction({
    actionArgs: act_4664876391460503_script_u7twxtn7m_getActionArgs,
  });
  const inst_evh_4664876391460503_input_lptr48hhc =
    new actionTypes.EventHandlerImpl({
      action: act_4664876391460503_script_u7twxtn7m,
      event: 'input',
      displayName: 'script',
    });
  const inst_evh_4664876391460503_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4664876391460503_input_lptr48hhc],
    event: 'input',
  });

  const actions = {
    inst_evh_7315092382398562_reload_cmlu1uerj,
    inst_evh_7315092382398562_reload,
    inst_evh_8497951412896552_success_rqbrjdfbh,
    inst_evh_8497951412896552_clickPreventStop_sx3oflgu4,
    fetchTaxpayer: inst_evh_8497951412896552_clickPreventStop_sx3oflgu4,
    inst_evh_8497951412896552_clickPreventStop,
    inst_evh_2014513287668498_input_o0681t3gg,
    inst_evh_2014513287668498_input,
    inst_evh_1490259005804208_input_twm0x1l0p,
    inst_evh_1490259005804208_input,
    inst_evh_5501809153536538_input_xubjnqcww,
    inst_evh_5501809153536538_input,
    inst_evh_1014170467214631_input_j4x293ri8,
    inst_evh_1014170467214631_input,
    inst_evh_4664876391460503_input_lptr48hhc,
    inst_evh_4664876391460503_input,
  };

  const evh_7315092382398562_reload_cmlu1uerj = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_cmlu1uerj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8497951412896552_success_rqbrjdfbh = async (event, scope) => {
    return await actions.inst_evh_8497951412896552_success_rqbrjdfbh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8497951412896552_clickPreventStop_sx3oflgu4 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8497951412896552_clickPreventStop_sx3oflgu4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8497951412896552_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8497951412896552_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2014513287668498_input_o0681t3gg = async (event, scope) => {
    return await actions.inst_evh_2014513287668498_input_o0681t3gg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2014513287668498_input = async (event, scope) => {
    return await actions.inst_evh_2014513287668498_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1490259005804208_input_twm0x1l0p = async (event, scope) => {
    return await actions.inst_evh_1490259005804208_input_twm0x1l0p.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1490259005804208_input = async (event, scope) => {
    return await actions.inst_evh_1490259005804208_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5501809153536538_input_xubjnqcww = async (event, scope) => {
    return await actions.inst_evh_5501809153536538_input_xubjnqcww.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5501809153536538_input = async (event, scope) => {
    return await actions.inst_evh_5501809153536538_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1014170467214631_input_j4x293ri8 = async (event, scope) => {
    return await actions.inst_evh_1014170467214631_input_j4x293ri8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1014170467214631_input = async (event, scope) => {
    return await actions.inst_evh_1014170467214631_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4664876391460503_input_lptr48hhc = async (event, scope) => {
    return await actions.inst_evh_4664876391460503_input_lptr48hhc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4664876391460503_input = async (event, scope) => {
    return await actions.inst_evh_4664876391460503_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpRow v-if="loaded">
      <ItpCol>
        <ItpFormGroup
          :label-cols-md="4"
          :content-cols-md="8"
          name="name"
          :label="pgettext('Company', 'Name')"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormInput name="name" v-model="item.name" required autofocus>
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          name="code"
          :label="pgettext('Company', 'Code')"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormInput name="code" v-model="item.code" required>
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          :content-cols-md="5"
          name="account_type"
          :label="gettext('Default')"
          :labelAlign="labelAlign"
        >
          <ItpFormCheckbox
            name="is_default"
            inline
            v-model="item.is_default"
            :readonly="item.isReadOnly"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          :content-cols-md="8"
          name="relation"
          :label="pgettext('Company', 'Type')"
          :labelAlign="labelAlign"
          required
          v-if="item.role === 'partner'"
        >
          <ItpFormNativeSelect
            name="relation"
            v-model="item.relation"
            required
            :options="companyRelations"
          >
          </ItpFormNativeSelect>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          :content-cols-md="8"
          name="groups"
          :label="pgettext('Company', 'Groups')"
          :labelAlign="labelAlign"
        >
          <ItpFormSearchableSelect
            name="groups"
            v-model="item.groups"
            multiple
            :options="companyGroups"
          >
          </ItpFormSearchableSelect>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          :content-cols-md="8"
          name="emails"
          :label="pgettext('Company', 'Email')"
          :labelAlign="labelAlign"
          :description="
            pgettext(
              'Company',
              'Multiple email addresses should be separated by semicolon.'
            )
          "
        >
          <ItpFormInput type="text" name="emails" v-model="item.emails">
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          :content-cols-md="8"
          name="external_key"
          :label="pgettext('Company', 'External key')"
          :labelAlign="labelAlign"
        >
          <ItpFormInput name="external_key" v-model="item.external_key">
          </ItpFormInput>
        </ItpFormGroup>
        <hr />
        <ItpFormGroup
          :label-cols-md="4"
          :content-cols-md="8"
          name="notes"
          :label="pgettext('Company', 'Notes')"
          :labelAlign="labelAlign"
        >
          <ItpFormTextarea name="notes" v-model="item.notes"> </ItpFormTextarea>
        </ItpFormGroup>
      </ItpCol>
      <ItpCol>
        <ItpAlert
          type="info"
          show
          class="offset-md-4"
          v-if="displayFetchByTaxNumber"
        >
          <ItpIcon fa="info-circle"> </ItpIcon>
          <span>
            {{
              pgettext(
                'Company',
                'By providing the tax number, the company data can be directly retrieved from the NAV database.'
              )
            }}
          </span>
        </ItpAlert>
        <ItpFormGroup
          :label-cols-md="4"
          name="tax_number"
          :label="pgettext('Company', 'Tax number')"
          :labelAlign="labelAlign"
        >
          <div>
            <ItpFormInput name="tax_number" v-model="item.tax_number">
            </ItpFormInput>
            <ItpButton
              variant="primary"
              :icon="$config.downloadIcon"
              :tooltip="
                pgettext('Company', 'Fetch taxpayer data from NAV database')
              "
              :disabled="!item.tax_number"
              :spinning="actions.fetchTaxpayer.isRunning"
              size="sm"
              :text="pgettext('Company', 'Retrieve data')"
              class="mt-3"
              v-if="displayFetchByTaxNumber && item.tax_number"
              @click.prevent.stop="
                evh_8497951412896552_clickPreventStop($event, {})
              "
            >
            </ItpButton>
          </div>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          name="tax_number"
          :label="pgettext('Company', 'Tax number history')"
          :labelAlign="labelAlign"
        >
          <ItpFormTextarea
            name="tax_number_history"
            v-model="item.tax_number_history"
            mode="list"
          >
          </ItpFormTextarea>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          name="group_member_tax_number"
          :label="pgettext('Company', 'Group member tax number')"
          :labelAlign="labelAlign"
        >
          <ItpFormInput
            name="group_member_tax_number"
            v-model="item.group_member_tax_number"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          name="eu_tax_number"
          :label="pgettext('Company', 'EU tax number')"
          :labelAlign="labelAlign"
        >
          <ItpFormInput name="eu_tax_number" v-model="item.eu_tax_number">
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          name="registration_number"
          :label="pgettext('Company', 'Registration number')"
          :labelAlign="labelAlign"
        >
          <ItpFormInput
            name="registration_number"
            v-model="item.registration_number"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <div v-if="isOwnCompany">
          <hr />
          <ItpFormGroup
            :label-cols-md="4"
            name="settings_send_invoice_notifications"
            :labelAlign="labelAlign"
          >
            <ItpFormCheckbox
              name="send_invoice_notifications"
              :label="pgettext('Company', 'Send invoice notifications')"
              v-model="settings.send_invoice_notifications"
              @input="evh_2014513287668498_input($event, {})"
            >
            </ItpFormCheckbox>
          </ItpFormGroup>
          <ItpFormGroup
            :label-cols-md="4"
            :content-cols-md="8"
            name="settings_smtp_mail_account_id"
            :labelAlign="labelAlign"
            :label="pgettext('Company', 'SMTP Mail Account')"
          >
            <UcMailAccountSelector
              :item="settings"
              path="smtp_mail_account_id"
              clearable
              :bindObject="b_1490259005804208_bindObject"
              @input="evh_1490259005804208_input($event, {})"
            >
            </UcMailAccountSelector>
          </ItpFormGroup>
        </div>
        <div v-if="!isOwnCompany">
          <hr />
          <ItpFormGroup
            name="payment_period_days"
            :label="pgettext('Company', 'Payment period')"
            :label-cols-md="4"
            :content-cols-md="5"
            :labelAlign="labelAlign"
            contentClass="input-group"
          >
            <ItpFormInput
              type="number"
              name="payment_period_days"
              v-model="item.payment_period_days"
            >
            </ItpFormInput>
            <span class="input-group-text">
              {{ pgettext('InputGroupAddon', 'days') }}
            </span>
          </ItpFormGroup>
          <ItpFormGroup
            name="discount"
            :label="pgettext('Company', 'Discount')"
            :label-cols-md="4"
            :content-cols-md="4"
            :labelAlign="labelAlign"
            contentClass="input-group"
          >
            <ItpFormInput type="number" name="discount" v-model="item.discount">
            </ItpFormInput>
            <span class="input-group-text"> % </span>
          </ItpFormGroup>
          <hr />
          <ItpFormGroup
            :label-cols-md="4"
            name="is_contractor"
            :labelAlign="labelAlign"
          >
            <ItpFormCheckbox
              name="is_contractor"
              :label="pgettext('Company', 'Contractor')"
              v-model="item.is_contractor"
            >
            </ItpFormCheckbox>
          </ItpFormGroup>
          <ItpFormGroup
            :label-cols-md="4"
            name="sole_proprietor"
            :labelAlign="labelAlign"
          >
            <ItpFormCheckbox
              name="sole_proprietor"
              :label="pgettext('Company', 'Sole proprietor')"
              v-model="item.sole_proprietor"
            >
            </ItpFormCheckbox>
          </ItpFormGroup>
          <ItpFormGroup
            :label-cols-md="4"
            name="is_natural_person"
            :labelAlign="labelAlign"
          >
            <ItpFormCheckbox
              name="is_natural_person"
              :label="pgettext('Company', 'Natural person')"
              v-model="item.is_natural_person"
            >
            </ItpFormCheckbox>
          </ItpFormGroup>
          <ItpFormGroup
            :label-cols-md="4"
            name="vat_exempt"
            :labelAlign="labelAlign"
          >
            <ItpFormCheckbox
              name="vat_exempt"
              :label="pgettext('Company', 'VAT exempt')"
              v-model="item.vat_exempt"
            >
            </ItpFormCheckbox>
          </ItpFormGroup>
          <ItpFormGroup
            :label-cols-md="4"
            name="excluded_from_claims"
            :labelAlign="labelAlign"
          >
            <ItpFormCheckbox
              name="excluded_from_claims"
              :label="pgettext('Company', 'Exclude from claims')"
              v-model="item.excluded_from_claims"
            >
            </ItpFormCheckbox>
          </ItpFormGroup>
          <hr />
          <ItpFormGroup
            :label-cols-md="4"
            name="settings_notifications_enabled"
            :labelAlign="labelAlign"
          >
            <ItpFormCheckbox
              name="settings__notifications_enabled"
              :label="pgettext('Company', 'Notifications enabled')"
              v-model="settings.notifications_enabled"
              @input="evh_5501809153536538_input($event, {})"
            >
            </ItpFormCheckbox>
          </ItpFormGroup>
          <ItpFormGroup
            :label-cols-md="4"
            name="settings_send_invoice_notifications"
            :labelAlign="labelAlign"
          >
            <ItpFormCheckbox
              name="settings__send_invoice_notifications"
              :label="pgettext('Company', 'Send invoice notifications')"
              v-model="settings.send_invoice_notifications"
              @input="evh_1014170467214631_input($event, {})"
            >
            </ItpFormCheckbox>
          </ItpFormGroup>
          <ItpFormGroup
            :label-cols-md="4"
            :content-cols-md="8"
            name="settings__email_recipients"
            :label="pgettext('Company', 'Email recipients')"
            :labelAlign="labelAlign"
            :description="
              pgettext('Company', 'Input one email address per row.')
            "
          >
            <ItpFormTextarea
              type="text"
              name="settings__email_recipients"
              v-model="settings.email_recipients"
              mode="list"
              @input="evh_4664876391460503_input($event, {})"
            >
            </ItpFormTextarea>
          </ItpFormGroup>
        </div>
      </ItpCol>
    </ItpRow>
  </ItpUsercontrol>
</template>
